import { EEventFormFieldType } from '__generated__/globalTypes';
import { EEventForm_fields } from 'graphql/fragment/__generated__/EEventForm';
import { Localization } from 'static/localization/localization';

const VALID_EMAIL_HASH = /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)*$/i;

interface FormSubmissionFieldError {
    type: 'error';
    message: string;
}

export interface FormSubmissionFieldValue {
    type: 'value';
    value: string | string[] | boolean | null;
}

type FormSubmissionFieldValueOrError = FormSubmissionFieldError | FormSubmissionFieldValue;

// Same validation is on BE, any changes need to be reflected there as well
export function validateFormFieldAndReturn(
    field: EEventForm_fields,
    __value: unknown
): FormSubmissionFieldValueOrError {
    const _value = (__value as string | string[] | boolean) ?? null;

    if (field.validation?.required) {
        if (
            field.type === EEventFormFieldType.EMAIL ||
            field.type === EEventFormFieldType.STRING ||
            field.type === EEventFormFieldType.RADIO ||
            field.type === EEventFormFieldType.STRING_MULTILINE ||
            field.type === EEventFormFieldType.URL ||
            field.type === EEventFormFieldType.IMAGE
        ) {
            const value = _value as string;
            if (!value || !value.trim()) {
                return { type: 'error', message: Localization.FIELD_REQUIRED };
            }
        }
        if (field.type === EEventFormFieldType.MULTI_OPTIONS) {
            const value = _value as string[];
            if (!value || !value.length) {
                return { type: 'error', message: Localization.FIELD_REQUIRED };
            }
        }

        if (field.type === EEventFormFieldType.BOOLEAN) {
            const value = _value as boolean;
            if (!value) {
                return { type: 'error', message: Localization.FIELD_REQUIRED };
            }
        }
    }

    if (!_value) {
        return { type: 'value', value: null };
    }

    if (field.validation?.maxLength) {
        const typesToCheck: EEventFormFieldType[] = [
            EEventFormFieldType.EMAIL,
            EEventFormFieldType.STRING,
            EEventFormFieldType.STRING_MULTILINE,
            EEventFormFieldType.URL,
            EEventFormFieldType.IMAGE
        ];
        if (typesToCheck.includes(field.type)) {
            const value = _value as string;
            if (value.length > field.validation.maxLength) {
                return { type: 'error', message: '~Max length exceeded' };
            }
        }
    }

    if (field.type === EEventFormFieldType.EMAIL) {
        const value = (_value as string)?.trim();
        const isInvalidEmail = !value.match(VALID_EMAIL_HASH);
        if (isInvalidEmail) {
            return { type: 'error', message: Localization.INVALID_EMAIL };
        }
    }

    if (field.type === EEventFormFieldType.RADIO) {
        const value = _value as string;
        const options = field?.options ?? [];
        const option = options.find((e) => e.optionId === value);
        if (!option) {
            return { type: 'error', message: Localization.INVALID_VALUE };
        }
        return { type: 'value', value: value };
    }

    if (field.type === EEventFormFieldType.MULTI_OPTIONS) {
        const value = _value as string[] | null;
        const options = field?.options ?? [];
        const selectedOptions = options.filter((e) => value && value.includes(e.optionId));
        return { type: 'value', value: selectedOptions.map((e) => e.optionId) };
    }

    if (
        field.type === EEventFormFieldType.STRING ||
        field.type === EEventFormFieldType.EMAIL ||
        field.type === EEventFormFieldType.STRING_MULTILINE ||
        field.type === EEventFormFieldType.URL ||
        field.type === EEventFormFieldType.IMAGE
    ) {
        const value = _value as string;
        return { type: 'value', value: value as string };
    }
    if (field.type === EEventFormFieldType.BOOLEAN) {
        const value = _value as boolean;
        return {
            type: 'value',
            value
        };
    }
}
